import type { ReactNode } from 'react'
import { COLORS } from '../../../../styles/theme'
import type { SurveyQuestion } from '@bandos/shared'
import type { SurveyAnswer } from '../../pages/surveyInsightsUtils'
import { INSIGHT_COLOR_YES, INSIGHT_COLOR_NO, INSIGHT_SCALE_COLORS } from './insightColors'
import {
  Card,
  CardHeader,
  QuestionMeta,
  QuestionNumber,
  QuestionText,
  TypeBadge,
  DeletedBadge,
  NoAnswer,
  QuoteCard,
  ScaleRow,
  ScaleDots,
  ScaleDot,
  ScaleValueLabel,
  YesNoPill,
  OptionList,
  OptionRow,
  OptionMarker,
  SourceBadgeRow,
  SourceBadge,
} from './IndividualResponseCardStyles'

export interface IndividualResponseCardProps {
  question: SurveyQuestion
  questionIndex: number
  answer: SurveyAnswer
  isDeleted?: boolean
}

const TYPE_COLORS: Record<string, string> = {
  scale: '#f59e0b',
  yes_no: '#22c55e',
  multiple_choice: COLORS.primary,
  checkbox: '#14b8a6',
  open: COLORS.purple500,
}

const TYPE_LABELS: Record<string, string> = {
  scale: 'Scale',
  yes_no: 'Yes / No',
  multiple_choice: 'Multiple choice',
  checkbox: 'Checkbox',
  open: 'Open text',
}

function renderScale(answer: SurveyAnswer, max: number): ReactNode {
  if (typeof answer !== 'number' || answer < 1 || answer > max) {
    return <NoAnswer>No answer</NoAnswer>
  }
  // Map an answer in [1..max] to one of the 5 palette colours so 1-10 ranges
  // reuse the same low→high gradient without needing a 10-colour palette.
  const colorIdx = Math.min(
    INSIGHT_SCALE_COLORS.length - 1,
    Math.floor(((answer - 1) * INSIGHT_SCALE_COLORS.length) / max)
  )
  const color = INSIGHT_SCALE_COLORS[colorIdx]
  const points = Array.from({ length: max }, (_, i) => i + 1)
  return (
    <ScaleRow>
      <ScaleDots>
        {points.map((v) => (
          <ScaleDot key={v} $active={v === answer} $color={color}>
            {v}
          </ScaleDot>
        ))}
      </ScaleDots>
      <ScaleValueLabel>
        {answer} / {max}
      </ScaleValueLabel>
    </ScaleRow>
  )
}

function renderYesNo(answer: SurveyAnswer): ReactNode {
  if (typeof answer !== 'boolean') return <NoAnswer>No answer</NoAnswer>
  return (
    <YesNoPill $color={answer ? INSIGHT_COLOR_YES : INSIGHT_COLOR_NO}>
      {answer ? 'Yes' : 'No'}
    </YesNoPill>
  )
}

function renderMultipleChoice(question: SurveyQuestion, answer: SurveyAnswer): ReactNode {
  const options = question.options ?? []
  if (typeof answer !== 'string' || !options.includes(answer)) {
    return <NoAnswer>No answer</NoAnswer>
  }
  return (
    <OptionList>
      {options.map((opt) => (
        <OptionRow key={opt} $selected={opt === answer}>
          <OptionMarker $selected={opt === answer} $shape="circle" />
          {opt}
        </OptionRow>
      ))}
    </OptionList>
  )
}

function renderCheckbox(question: SurveyQuestion, answer: SurveyAnswer): ReactNode {
  const options = question.options ?? []
  const selected = Array.isArray(answer)
    ? answer.filter((v): v is string => typeof v === 'string' && options.includes(v))
    : []
  if (selected.length === 0) return <NoAnswer>No answer</NoAnswer>
  return (
    <OptionList>
      {options.map((opt) => {
        const isSelected = selected.includes(opt)
        return (
          <OptionRow key={opt} $selected={isSelected}>
            <OptionMarker $selected={isSelected} $shape="square" />
            {opt}
          </OptionRow>
        )
      })}
    </OptionList>
  )
}

function renderOpen(answer: SurveyAnswer): ReactNode {
  if (typeof answer !== 'string' || answer.trim().length === 0) {
    return <NoAnswer>No answer</NoAnswer>
  }
  return <QuoteCard>{answer}</QuoteCard>
}

function renderAnswer(question: SurveyQuestion, answer: SurveyAnswer): ReactNode {
  switch (question.type) {
    case 'scale':
      return renderScale(answer, question.scaleMax ?? 5)
    case 'yes_no':
      return renderYesNo(answer)
    case 'multiple_choice':
      return renderMultipleChoice(question, answer)
    case 'checkbox':
      return renderCheckbox(question, answer)
    case 'open':
      return renderOpen(answer)
    default:
      return <NoAnswer>No answer</NoAnswer>
  }
}

function sourceLabelsFor(question: SurveyQuestion): string[] {
  if (question.sourceNodeLabels && question.sourceNodeLabels.length > 0) {
    return question.sourceNodeLabels.filter((l): l is string => Boolean(l && l.length > 0))
  }
  if (question.sourceNodeLabel) return [question.sourceNodeLabel]
  return []
}

export function IndividualResponseCard({
  question,
  questionIndex,
  answer,
  isDeleted = false,
}: IndividualResponseCardProps) {
  const color = TYPE_COLORS[question.type] ?? COLORS.primary
  const sourceLabels = sourceLabelsFor(question)

  return (
    <Card $deleted={isDeleted}>
      <CardHeader>
        <QuestionMeta>
          {!isDeleted && <QuestionNumber>Q{questionIndex + 1}</QuestionNumber>}
          <QuestionText>{question.question}</QuestionText>
          {sourceLabels.length > 0 && (
            <SourceBadgeRow>
              {sourceLabels.map((label, i) => (
                <SourceBadge key={`${label}-${i}`} title={label}>
                  {label}
                </SourceBadge>
              ))}
            </SourceBadgeRow>
          )}
        </QuestionMeta>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
          {isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
          <TypeBadge $color={color}>{TYPE_LABELS[question.type] ?? question.type}</TypeBadge>
        </div>
      </CardHeader>

      {renderAnswer(question, answer)}
    </Card>
  )
}
